import React, { useEffect, useState } from 'react';
import {
  COMPETENCE_LIMITS,
  hasCompetenceScope,
  normalizeCompetenceProfile,
  type CompetenceNotifyPreferences,
  type CompetenceProfile,
} from '@zunelo/shared';
import type { UserSettingsPublic, UserSettingsUpdate } from '../../services/clients/types';
import { notificationsClient } from '../../services/clients';
import {
  enablePushNotifications,
  getPushSupportState,
  isPushSupported,
  type PushSupportState,
} from '../../pwa/pushSubscription';
import { playAlertTone } from '../../pwa/alertTone';
import { Button } from '../../ui/common/Button';
import {
  buttonRowStyle,
  hintStyle,
  inputStyle,
  labelStyle,
  sectionStyle,
  statusChipStyle,
} from './settingsStyles';

export interface SettingsAlertsSectionProps {
  settings: UserSettingsPublic;
  saving: boolean;
  onSavePatch: (
    patch: UserSettingsUpdate,
    successMessage: string,
    after?: () => void,
  ) => Promise<void>;
}

const PUSH_STATE_LABEL: Record<PushSupportState, string> = {
  unsupported: 'No disponible en este navegador',
  denied: 'Bloqueado por el navegador',
  subscribed: 'Activos en este dispositivo',
  'not-subscribed': 'No activados en este dispositivo',
};

const NOTIFY_LABELS: Array<{
  key: keyof CompetenceNotifyPreferences;
  label: string;
  hint: string;
}> = [
  { key: 'mentions', label: 'Menciones', hint: 'Cuando te mencionan o te nombran en Slack.' },
  { key: 'directMessages', label: 'Mensajes directos', hint: 'DMs nuevos en Slack.' },
  {
    key: 'threadsWaitingOnMe',
    label: 'Hilos que esperan tu respuesta',
    hint: 'Hilos en los que participas y te toca responder.',
  },
  {
    key: 'competenceMatches',
    label: 'Temas de tu ámbito',
    hint: 'Palabras clave, canales, proyectos o repos declarados abajo.',
  },
  {
    key: 'reviewRequests',
    label: 'Solicitudes de review en GitHub',
    hint: 'Cuando te piden revisar un pull request.',
  },
];

const LIST_FIELDS: Array<{
  key: 'keywords' | 'slackChannels' | 'jiraProjects' | 'githubRepos';
  label: string;
  placeholder: string;
  hint: string;
}> = [
  {
    key: 'keywords',
    label: 'Palabras clave',
    placeholder: 'billing, checkout, refund flow',
    hint: `Temas que son tuyos. Coincidencia por palabra completa, sin distinguir acentos. Máx. ${COMPETENCE_LIMITS.keywords}.`,
  },
  {
    key: 'slackChannels',
    label: 'Canales de Slack',
    placeholder: 'eng-billing, payments-alerts',
    hint: 'Sin #. Los hilos nuevos en estos canales se marcan como de tu ámbito; solo avisan si piden algo.',
  },
  {
    key: 'jiraProjects',
    label: 'Proyectos Jira',
    placeholder: 'DEV, OPS',
    hint: 'Claves de proyecto. Cualquier ticket de estos proyectos citado en Slack cuenta.',
  },
  {
    key: 'githubRepos',
    label: 'Repositorios',
    placeholder: 'genlogs/carrier-web',
    hint: 'owner/repo o solo repo.',
  },
];

function listToText(values: string[]): string {
  return values.join(', ');
}

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  fontSize: 13,
  color: 'var(--z-text-3)',
};

function PushControls({ saving }: { saving: boolean }): React.ReactElement {
  // Resolve the definitive "unsupported" answer synchronously so environments
  // without Web Push (jsdom, old Safari) never flash "Comprobando…".
  const [pushState, setPushState] = useState<PushSupportState | 'checking'>(() =>
    isPushSupported() ? 'checking' : 'unsupported',
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (pushState !== 'checking') return;
    let cancelled = false;
    void getPushSupportState().then((state) => {
      if (!cancelled) setPushState(state);
    });
    return () => {
      cancelled = true;
    };
    // Only the initial probe: later transitions come from user actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enable = async () => {
    setBusy(true);
    setMessage(null);
    const result = await enablePushNotifications(notificationsClient);
    if (result.ok) {
      setPushState('subscribed');
      setMessage(
        result.alreadySubscribed
          ? 'Este dispositivo ya estaba suscrito; registro renovado.'
          : 'Avisos activados en este dispositivo.',
      );
    } else if (result.reason === 'denied') {
      setPushState('denied');
      setMessage(
        'El navegador bloqueó las notificaciones. Permítelas en la configuración del sitio y reintenta.',
      );
    } else if (result.reason === 'unsupported') {
      setPushState('unsupported');
      setMessage(
        'Este navegador no soporta Web Push. En iOS instala Zunelo como app desde Compartir → Añadir a inicio.',
      );
    } else {
      setMessage(
        result.message ? `No se pudo activar: ${result.message}` : 'No se pudo activar los avisos.',
      );
    }
    setBusy(false);
  };

  const sendTest = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await notificationsClient.sendTestNotification();
      setMessage(
        res.data.sent
          ? 'Aviso de prueba enviado. Debería aparecer en unos segundos.'
          : 'No hay dispositivos suscritos para recibir la prueba.',
      );
    } catch (err) {
      setMessage(
        err instanceof Error
          ? `No se pudo enviar la prueba: ${err.message}`
          : 'No se pudo enviar la prueba.',
      );
    }
    setBusy(false);
  };

  const subscribed = pushState === 'subscribed';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={labelStyle}>Avisos en este dispositivo</div>
        <span data-testid="settings-alerts-push-chip" style={statusChipStyle(subscribed)}>
          {pushState === 'checking' ? 'Comprobando…' : PUSH_STATE_LABEL[pushState]}
        </span>
      </div>
      <p style={hintStyle}>
        Zunelo te avisa aunque la pestaña esté cerrada: menciones, hilos que esperan tu respuesta,
        temas de tu ámbito y solicitudes de review. Sin ruido: el resto de la actividad solo aparece
        en el Radar.
      </p>
      <div style={buttonRowStyle}>
        <Button
          size="sm"
          variant="primary"
          data-testid="settings-alerts-push-enable"
          disabled={busy || saving || pushState === 'unsupported' || pushState === 'checking'}
          onClick={() => void enable()}
        >
          {subscribed ? 'Renovar registro' : 'Activar avisos'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          data-testid="settings-alerts-push-test"
          disabled={busy || saving || !subscribed}
          onClick={() => void sendTest()}
        >
          Enviar aviso de prueba
        </Button>
        <Button
          size="sm"
          variant="ghost"
          data-testid="settings-alerts-sound-test"
          onClick={() => playAlertTone()}
        >
          Probar sonido
        </Button>
      </div>
      <p style={hintStyle}>
        Los navegadores no permiten personalizar el sonido de la notificación del sistema — siempre
        suena el timbre nativo del sistema operativo. Este botón previsualiza el tono adicional que
        Zunelo reproduce cuando la pestaña está abierta y llega un aviso urgente.
      </p>
      {message ? (
        <div
          data-testid="settings-alerts-push-message"
          style={{ fontSize: 12.5, color: 'var(--z-text-3)' }}
        >
          {message}
        </div>
      ) : null}
    </div>
  );
}

export const SettingsAlertsSection: React.FC<SettingsAlertsSectionProps> = ({
  settings,
  saving,
  onSavePatch,
}) => {
  const [draft, setDraft] = useState<CompetenceProfile>(settings.competence);
  const [listText, setListText] = useState<Record<string, string>>(() => ({
    keywords: listToText(settings.competence.keywords),
    slackChannels: listToText(settings.competence.slackChannels),
    jiraProjects: listToText(settings.competence.jiraProjects),
    githubRepos: listToText(settings.competence.githubRepos),
  }));

  // Every save (even a bare notify toggle) hands the section a fresh
  // `settings` object. Re-sync the scope inputs only when the *stored lists*
  // actually changed (a Guardar ámbito round-trip or another device), never on
  // an unrelated re-render — otherwise half-typed text would be wiped.
  const storedListsKey = JSON.stringify([
    settings.competence.keywords,
    settings.competence.slackChannels,
    settings.competence.jiraProjects,
    settings.competence.githubRepos,
  ]);
  const storedNotifyKey = JSON.stringify(settings.competence.notifyOn);
  useEffect(() => {
    setListText({
      keywords: listToText(settings.competence.keywords),
      slackChannels: listToText(settings.competence.slackChannels),
      jiraProjects: listToText(settings.competence.jiraProjects),
      githubRepos: listToText(settings.competence.githubRepos),
    });
    setDraft((prev) => ({ ...prev, ...settings.competence, notifyOn: prev.notifyOn }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedListsKey]);
  useEffect(() => {
    setDraft((prev) => ({ ...prev, notifyOn: settings.competence.notifyOn }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedNotifyKey]);

  const normalizedDraft = normalizeCompetenceProfile({ ...draft, ...listText });
  const dirty = JSON.stringify(normalizedDraft) !== JSON.stringify(settings.competence);

  // Toggles persist only the preference on top of the *stored* scope lists:
  // half-typed scope text stays a draft until the user presses Guardar ámbito.
  const saveNotify = (key: keyof CompetenceNotifyPreferences, value: boolean) => {
    const notifyOn = { ...normalizedDraft.notifyOn, [key]: value };
    const next = normalizeCompetenceProfile({ ...settings.competence, notifyOn });
    setDraft((prev) => ({ ...prev, notifyOn }));
    void onSavePatch({ competence: next }, 'Preferencias de aviso actualizadas');
  };

  const saveProfile = () => {
    void onSavePatch({ competence: normalizedDraft }, 'Perfil de ámbito guardado', () => {
      setDraft(normalizedDraft);
    });
  };

  return (
    <section style={sectionStyle} data-testid="settings-alerts-section">
      <PushControls saving={saving} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        <div style={labelStyle}>Qué te avisa</div>
        {NOTIFY_LABELS.map(({ key, label, hint }) => (
          <label key={key} style={checkboxRowStyle}>
            <input
              type="checkbox"
              aria-label={label}
              data-testid={`settings-alerts-notify-${key}`}
              checked={normalizedDraft.notifyOn[key]}
              disabled={saving}
              onChange={(e) => saveNotify(key, e.target.checked)}
            />
            <span>
              <span style={{ fontWeight: 600, color: 'var(--z-text-2)' }}>{label}</span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--z-dim)' }}>{hint}</span>
            </span>
          </label>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={labelStyle}>Tu ámbito de competencia</div>
          <span
            data-testid="settings-alerts-scope-chip"
            style={statusChipStyle(hasCompetenceScope(settings.competence))}
          >
            {hasCompetenceScope(settings.competence) ? 'Definido' : 'Sin definir'}
          </span>
        </div>
        <p style={hintStyle}>
          Zunelo busca estos temas en Slack cada 15 minutos, aunque nadie te mencione, y marca las
          tarjetas con <strong>Tu ámbito</strong>. Separa los valores con comas.
        </p>
        {LIST_FIELDS.map(({ key, label, placeholder, hint }) => (
          <label
            key={key}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: 'var(--z-dim)',
            }}
          >
            <span style={{ fontWeight: 600, color: 'var(--z-text-2)', fontSize: 12.5 }}>
              {label}
            </span>
            <input
              type="text"
              data-testid={`settings-alerts-${key}`}
              style={inputStyle}
              placeholder={placeholder}
              value={listText[key]}
              disabled={saving}
              onChange={(e) => setListText((prev) => ({ ...prev, [key]: e.target.value }))}
            />
            <span>{hint}</span>
          </label>
        ))}
        <div style={buttonRowStyle}>
          <Button
            size="sm"
            variant="primary"
            data-testid="settings-alerts-save-profile"
            disabled={saving || !dirty}
            onClick={saveProfile}
          >
            Guardar ámbito
          </Button>
        </div>
      </div>
    </section>
  );
};
